<template>
    <div class="timeline-wrapper">
        <!-- Time display -->
        <div class="d-flex justify-content-between text-muted small mb-1">
            <span>{{ formatTime(globalTime) }}</span>
            <span>{{ formatTime(totalDuration) }}</span>
        </div>

        <!-- Timeline track -->
        <div
            ref="track"
            class="timeline-track"
            @mousedown="onTrackMouseDown"
            @touchstart.prevent="onTrackTouchStart"
        >
            <!-- Buffered segments indicator -->
            <div
                v-for="index in bufferedIndices"
                :key="'buf-' + index"
                class="timeline-buffered"
                :style="getSegmentStyle(index)"
            />

            <!-- Progress bar -->
            <div
                class="timeline-progress"
                :style="{ width: progressPercent + '%' }"
            />

            <!-- Segment tick marks -->
            <div
                v-for="(seg, i) in segments"
                :key="'tick-' + i"
                class="timeline-tick"
                :style="{ left: getTickPosition(seg) + '%' }"
            />

            <!-- Playhead -->
            <div
                class="timeline-playhead"
                :style="{ left: progressPercent + '%' }"
            />
        </div>
    </div>
</template>

<script>
export default {
    name: 'VideoTimeline',
    props: {
        globalTime: {
            type: Number,
            default: 0
        },
        totalDuration: {
            type: Number,
            default: 0
        },
        segments: {
            type: Array,
            default: () => []
        },
        bufferedIndices: {
            type: Array,
            default: () => []
        },
        isPlaying: {
            type: Boolean,
            default: false
        }
    },
    emits: ['seek'],
    data() {
        return {
            isDragging: false
        }
    },
    computed: {
        progressPercent() {
            if (!this.totalDuration) return 0
            return Math.min((this.globalTime / this.totalDuration) * 100, 100)
        }
    },
    beforeUnmount() {
        this.removeListeners()
    },
    methods: {
        getSegmentStyle(index) {
            if (index < 0 || index >= this.segments.length || !this.totalDuration) return {}
            const seg = this.segments[index]
            const left = (seg.startTime / this.totalDuration) * 100
            const width = (seg.duration / this.totalDuration) * 100
            return {
                left: left + '%',
                width: width + '%'
            }
        },

        getTickPosition(segment) {
            if (!this.totalDuration) return 0
            return (segment.startTime / this.totalDuration) * 100
        },

        getTimeFromEvent(event) {
            const track = this.$refs.track
            if (!track) return 0

            const rect = track.getBoundingClientRect()
            const clientX = event.touches ? event.touches[0].clientX : event.clientX
            const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
            const percent = x / rect.width
            return percent * this.totalDuration
        },

        onTrackMouseDown(event) {
            this.isDragging = true
            const time = this.getTimeFromEvent(event)
            this.$emit('seek', time)

            document.addEventListener('mousemove', this.onMouseMove)
            document.addEventListener('mouseup', this.onMouseUp)
        },

        onMouseMove(event) {
            if (!this.isDragging) return
            const time = this.getTimeFromEvent(event)
            this.$emit('seek', time)
        },

        onMouseUp() {
            this.isDragging = false
            this.removeListeners()
        },

        onTrackTouchStart(event) {
            this.isDragging = true
            const time = this.getTimeFromEvent(event)
            this.$emit('seek', time)

            document.addEventListener('touchmove', this.onTouchMove)
            document.addEventListener('touchend', this.onTouchEnd)
        },

        onTouchMove(event) {
            if (!this.isDragging) return
            const time = this.getTimeFromEvent(event)
            this.$emit('seek', time)
        },

        onTouchEnd() {
            this.isDragging = false
            this.removeListeners()
        },

        removeListeners() {
            document.removeEventListener('mousemove', this.onMouseMove)
            document.removeEventListener('mouseup', this.onMouseUp)
            document.removeEventListener('touchmove', this.onTouchMove)
            document.removeEventListener('touchend', this.onTouchEnd)
        },

        formatTime(seconds) {
            if (!seconds || !isFinite(seconds)) return '00:00'
            const totalSec = Math.floor(seconds)
            const h = Math.floor(totalSec / 3600)
            const m = Math.floor((totalSec % 3600) / 60)
            const s = totalSec % 60
            if (h > 0) {
                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
            }
            return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
        }
    }
}
</script>

<style scoped>
.timeline-track {
    position: relative;
    height: 8px;
    background: #e0e0e0;
    border-radius: 4px;
    cursor: pointer;
    user-select: none;
}

.timeline-progress {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: #7FC000;
    border-radius: 4px;
    pointer-events: none;
    z-index: 2;
}

.timeline-buffered {
    position: absolute;
    top: 0;
    height: 100%;
    background: rgba(127, 192, 0, 0.25);
    border-radius: 4px;
    pointer-events: none;
    z-index: 1;
}

.timeline-tick {
    position: absolute;
    top: -2px;
    width: 1px;
    height: 12px;
    background: rgba(0, 0, 0, 0.2);
    pointer-events: none;
    z-index: 3;
}

.timeline-tick:first-child {
    display: none;
}

.timeline-playhead {
    position: absolute;
    top: -4px;
    width: 14px;
    height: 14px;
    margin-left: -7px;
    background: #7FC000;
    border: 2px solid #fff;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    pointer-events: none;
    z-index: 4;
}
</style>
